/**
 * The input components
 */
import * as React from 'react';
import * as typestyle from 'typestyle';
import * as csstips from 'csstips';
import { colors, fontFamily } from './styles';
import { ButtonStyles, PrimaryButton } from './button';
import { Search as SearchIcon } from './icons';
import { LoaderStyles, LoaderWhite, Loader } from './loader';

export enum InputType {
  email,
  number,
  password,
  text,
  tel,
  url,
}

export interface TextInputProps {
  id?: string,
  name?: string,
  type?: InputType,
  placeholder?: string,
  readOnly?: boolean,
  takeFocus?: boolean,
  minLength?: number,
  maxLength?: number,
  autoFocus?: boolean,
  className?: string,

  value: string,
  onChange: (value: string) => any,

  error?: string;

  onFocus?: () => any,
  onBlur?: () => any,
  onKeyDown?: (e: React.SyntheticEvent<any>) => any,
  onKeyUp?: (e: React.SyntheticEvent<any>) => any,
  onKeyPress?: (e: React.SyntheticEvent<any>) => any

}

/**
 * Built it for https://app.frontify.com/d/zYF6HCW0aIp5/ui-library#/atoms/buttons
 */
export namespace InputStyles {
  export const color = colors.grey;
  export const borderColorNormal = colors.warmGrey3;
  export const borderColorOnFocus = colors.warmGrey4;
  export const errorColor = colors.red;

  export const root = typestyle.extend(
    csstips.flexRoot,
    csstips.flex,
    {
      width: '100%',

      height: 40,
      lineHeight: 'normal',
      padding: '7px 10px',
      color: color,
      border: `1px solid ${borderColorNormal}`,
      background: '#fff',
      borderRadius: 3,
      outline: 'none',

      transition: 'color .2s',

      fontFamily: fontFamily.arial,
    });

  export const rootClassName = typestyle.style(root, {
    $nest: {
      '&:hover': {
        borderColor: borderColorOnFocus,
      },
      '&:focus': {
        borderColor: borderColorOnFocus,
        borderWidth: 2,
        padding: '6px 9px'
      }
    }
  });
};

export class Input extends React.Component<TextInputProps, {}>{
  refs: {
    [key: string]: any;
    input: HTMLInputElement;
  }

  theInput: HTMLInputElement;

  componentDidMount(){
    if (!!this.props.autoFocus) {
      setCaretToEndOfText(this.theInput);
    }
  }

  render() {
    const style = this.props.error
      ? {
        color: InputStyles.errorColor,
        borderColor: InputStyles.errorColor
      }
      : {};

    return <input
      id={this.props.id}
      name={this.props.name}
      ref={(input) => { this.theInput = input; }}
      className={(InputStyles.rootClassName + ' ' + (this.props.className || ''))}
      style={style}
      type={InputType[this.props.type || InputType.text]}
      placeholder={this.props.placeholder}
      value={this.props.value || ''}
      minLength={this.props.minLength}
      maxLength={this.props.maxLength}
      readOnly={this.props.readOnly}
      onChange={this.handleChange}
      onFocus={this.props.onFocus}
      onBlur={this.props.onBlur}
      onKeyDown={this.props.onKeyDown}
      onKeyUp={this.props.onKeyUp}
      onKeyPress={this.props.onKeyPress}
      autoFocus={this.props.autoFocus}
      />;
  }

  handleChange = (evt: React.FormEvent<HTMLInputElement>) => {
    let value = (evt.target as HTMLInputElement).value;
    this.props.onChange(value);
  }
}

export class TextArea extends React.Component<TextInputProps, {}>{
  refs: {
    [key: string]: any;
    input: HTMLInputElement;
  }

  theInput: HTMLInputElement;

  componentDidMount(){
    if (this.props.takeFocus) {
      this.theInput.focus();
    }
  }

  render() {
    const minHeight = '100px';
    const style = this.props.error
      ? {
        color: InputStyles.errorColor,
        borderColor: InputStyles.errorColor,
        minHeight,
        resize: 'none' as 'none',
      }
      : {
        minHeight,
        resize: 'none' as 'none',
      };

    return <textarea
      id={this.props.id}
      name={this.props.name}
      className={(InputStyles.rootClassName + ' ' + (this.props.className || ''))}
      style={style}
      placeholder={this.props.placeholder}
      value={this.props.value || ''}
      minLength={this.props.minLength}
      maxLength={this.props.maxLength}
      readOnly={this.props.readOnly}
      onChange={this.handleChange}
      onFocus={this.props.onFocus}
      onBlur={this.props.onBlur}
      onKeyDown={this.onKeyDown}
      autoFocus={this.props.autoFocus}
      />;
  }

  handleChange = (evt: React.FormEvent<HTMLTextAreaElement>) => {
    let value = (evt.target as HTMLTextAreaElement).value;
    this.props.onChange(value);
  }

  onKeyDown = (evt: React.FormEvent<HTMLTextAreaElement>) => {
    let event: KeyboardEvent = evt as any; // treat evt as a native keyboard event.
    if (event.key == 'Enter') {
      event.stopPropagation();
      event.preventDefault();
      const value = (evt.target as HTMLTextAreaElement).value + '\n';
      this.props.onChange(value);
    }
    /** Still call original */
    this.props.onKeyDown && this.props.onKeyDown(evt);
  }
}

interface SubmitInputProps {
  id?: string;
  name?: string;

  value: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: any;
}

interface SubmitButtonProps {
  id?: string;
  name?: string;

  value: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: any;
}

/**
 * This is an input styled as a `button` for form submits
 */
export class SubmitInputPrimary extends React.Component<SubmitInputProps, {}>{
  render() {
    const disabled = typeof this.props.disabled === 'undefined' ? false : this.props.disabled;

    return !this.props.loading
      ? <input
        id={this.props.id}
        name={this.props.name}
        className={ButtonStyles.primaryClassName}
        type={"submit"}
        value={this.props.value}
        disabled={disabled}
        onClick={this.props.onClick}
        />
      : <button type="submit" className={ButtonStyles.loadingClassName} disabled={true}>
        <span style={{ color: 'transparent' }}>{this.props.value}</span>
        <div style={{ position: 'absolute', left: '43%', top: '25%' }}><LoaderWhite /></div>
      </button>;
  }
}

export class SubmitInputSecondary extends React.Component<SubmitInputProps, {}>{
  render() {
    const disabled = typeof this.props.disabled === 'undefined' ? false : this.props.disabled;

    return !this.props.loading
      ? <input
        id={this.props.id}
        name={this.props.name}
        className={ButtonStyles.secondaryClassName}
        type={"submit"}
        value={this.props.value}
        disabled={disabled}
        />
      : <button className={ButtonStyles.loadingClassName} disabled={true}>
        <span style={{ color: 'transparent' }}>{this.props.value}</span>
        <div style={{ position: 'absolute', left: '43%', top: '25%' }}><LoaderWhite /></div>
      </button>;
  }
}

export class SubmitButtonPrimary extends React.Component<SubmitButtonProps, {}>{
  render() {
    const disabled = this.props.loading || (typeof this.props.disabled === 'undefined' ? false : this.props.disabled);

    return <button
        id={this.props.id}
        name={this.props.name}
        className={ButtonStyles.primaryClassName}
        disabled={disabled}
        value={this.props.value}
        onClick={this.props.onClick}
      >
        <span>{this.props.value}</span>
      </button>;
  }
}


export interface CheckBoxProps {
  id?: string,
  name?: string,
  disabled?: boolean,

  value: boolean,
  onChange: (value: boolean) => any,

  error?: string;
}

/**
 * This is an input styled as a `checkbox`
 * https://app.frontify.com/d/zYF6HCW0aIp5/ui-library#/forms/checkboxes
 */
export namespace CheckboxStyles {
  export const primaryClassName = typestyle.style({
    $nest: {
      '&:focus': {
        outline: `${colors.warmGrey4} dotted thin`
      }
    }
  });
}

export class Checkbox extends React.Component<CheckBoxProps, {}>{
  render() {
    const disabled = typeof this.props.disabled === 'undefined' ? false : this.props.disabled;

    return <input
      id={this.props.id}
      name={this.props.name}
      className={CheckboxStyles.primaryClassName}
      type={"checkbox"}
      disabled={disabled}

      checked={this.props.value}
      onChange={
        (evt: React.FormEvent<any>) => {
          let value = (evt.target as HTMLInputElement).checked;
          this.props.onChange(value);
        }
      }
      />;
  }
}

namespace SearchInputStyles {
  export const searchButton = typestyle.style(
    /** Clear any button style */
    {
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      $nest: {
        '&:focus': {
          outline: 'thin dotted',
          outlineColor: colors.warmGrey4
        }
      }
    },
    /** Position it relative to input */
    {
      position: 'absolute',
      right: '4px',
      top: '4px'
    }
  );

  export const input = typestyle.style({
    /** Leave some space for the search button */
    paddingRight: '40px  !important',

    /**
     * Also change some defaults so they match the :focus styles. Otherwise the search button jumps a bit
     **/
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '9px',
    borderWidth: '2px'
  });
}

type SearchInputProps = {
  value: string,
  onChange(value: string): void,
  /**
   * Called if
   * - user click on search button
   * - user presses enter
   */
  onSearch(value: string): void,
}
export class SearchInput extends React.Component<SearchInputProps, {}> {
  render() {
    const {props} = this;
    return (
      <div style={{ position: 'relative' }}>
        <Input
          value={props.value}
          onChange={this.handleChange}
          placeholder="Search"
          className={SearchInputStyles.input}
          onKeyDown={(e) => {
            let {enter} = getKeyStates(e);
            if (enter) {
              this.handleSearch();
            }
          } } />
        <button className={SearchInputStyles.searchButton} onClick={this.handleSearch}><SearchIcon /></button>
      </div>
    );
  }

  handleChange = (value: string) => {
    this.props.onChange(value);
  }

  handleSearch = () => this.props.onSearch(this.props.value);
}

function setCaretToEndOfText(theInput) {
  var inputEndPos =  theInput.value.length;
  theInput.setSelectionRange(inputEndPos, inputEndPos);
}

/** Utility function for keyboard handling */
export function getKeyStates(e: React.SyntheticEvent<any>) {
  let event: KeyboardEvent = e as any; // This is a lie .... but a convinient one as react provides the same stuff
  let nativeEvent: KeyboardEvent = e.nativeEvent as any; // This is the truth

  let tab = event.key == 'Tab';
  let shift = nativeEvent.shiftKey;
  let mod = nativeEvent.metaKey || nativeEvent.ctrlKey;
  let enter = event.key == 'Enter';
  let up = event.key == 'ArrowUp';
  let down = event.key == 'ArrowDown';

  let tabNext = tab && !shift;
  let tabPrevious = tab && shift;

  return { tab, tabNext, tabPrevious, up, down, shift, mod, enter };
}
